// How the logbook pages show their entry lists, persisted in localStorage so
// the choice sticks across logbooks and sessions. useLayerPrefs' shape: the
// defaults are spread UNDER the stored object, so adding a key here turns it
// on/off for everyone who never touched the control while an explicit choice
// survives -- no storage-key bump when this list grows.
//
// The stored object is NOT a sparse record of choices: setPref writes a full
// snapshot of {...defaults, ...stored}, so once a key has been written the
// default no longer reaches that user. Two consequences. A new key picks up its
// default for everyone, including people who have toggled something else --
// which is why showPhotos could be added defaulting to TRUE without a storage
// bump, showNotes being the only key any release had ever written. And it is a
// ONE-WAY DOOR: flipping an existing default from false to true is free, from
// true to false is not, because everyone who has touched any switch since would
// keep the old value forever. That one needs a storage-key bump.
//
// The Entries/Trips view toggle is deliberately NOT here: it lives in the URL
// (`?view=`), because a shared link or the Back button should land on the view
// the user was looking at -- and the big log and the year page have OPPOSITE
// defaults, so one stored preference could not serve both.

import { useState } from 'react';

const STORAGE_KEY = 'tp.logbook.view.v1';

export const VIEW_PREF_DEFAULTS = {
  // Off: the lists keep their compact "has notes" icon column. On: each noted
  // entry carries its note in full on a continuation row.
  showNotes: false,
  // On: an entry's photos ride that same continuation row. See below for why a
  // default of TRUE is safe here and is a one-way door.
  showPhotos: true,
};

function load() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const stored = raw ? JSON.parse(raw) : {};
    return { ...VIEW_PREF_DEFAULTS, ...(typeof stored === 'object' && stored ? stored : {}) };
  } catch {
    return { ...VIEW_PREF_DEFAULTS };
  }
}

export function useLogbookViewPrefs() {
  const [prefs, setPrefs] = useState(load);

  function setPref(key, value) {
    setPrefs((current) => {
      const next = { ...current, [key]: Boolean(value) };
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Storage full/blocked: the toggle still works for this session.
      }
      return next;
    });
  }

  return [prefs, setPref];
}

export default useLogbookViewPrefs;
